import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import JourneyPosition from "@/components/JourneyPosition";
import TalentaCTA from "@/components/TalentaCTA";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Sparkles, Compass, FileText, ClipboardCheck, Star, BookOpen, Route, ArrowRight, ListChecks, Check, Plus, Save, GraduationCap } from "lucide-react";

export default function SusunLangkah() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [favorite, setFavorite] = useState(null);
  const [rencanaSel, setRencanaSel] = useState([]);
  const [customStep, setCustomStep] = useState("");
  const [savingRencana, setSavingRencana] = useState(false);
  const [mapelOpts, setMapelOpts] = useState([]);
  const [mapelSel, setMapelSel] = useState([]);
  const [partner, setPartner] = useState(null);
  const [savingMapel, setSavingMapel] = useState(false);
  const [targetPtn, setTargetPtn] = useState("");
  const [targetProdi, setTargetProdi] = useState("");
  const [savingTarget, setSavingTarget] = useState(false);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/profesi/favorite").then((r) => setFavorite(r.data.favorite)).catch(() => {});
    api.get("/journey/content").then((r) => setMapelOpts(r.data.mapel_pilihan || [])).catch(() => {});
    api.get("/assessment/partner").then((r) => setPartner(r.data.report)).catch(() => {});
  }, []);

  const savedRencana = data?.journey?.rencana || [];
  useEffect(() => { if (data?.journey?.rencana?.length) setRencanaSel(data.journey.rencana); }, [data]);
  useEffect(() => { if (data?.journey?.mapel_pilihan?.length) setMapelSel(data.journey.mapel_pilihan); }, [data]);
  useEffect(() => { const t = data?.journey?.target_pendidikan; if (t) { setTargetPtn(t.ptn || ""); setTargetProdi(t.prodi || ""); } }, [data]);

  const kelas = user?.kelas;
  const hasAssessment = !!data?.assessment;
  const hasMapel = !!data?.journey?.mapel_pilihan?.length;
  const hasBidang = !!data?.journey?.bidang_minat;
  const hasTryout = data?.tryout_count > 0;
  const n = data?.assessment?.narrative;
  const aiSteps = n?.saran_langkah || [];
  const mapelPrioritas = n?.mapel_prioritas || [];
  const jalurRek = n?.jalur_rekomendasi;
  const prodiRek = n?.prodi_rekomendasi?.length ? n.prodi_rekomendasi : (partner?.data?.rekomendasi_jurusan_ptn || []);
  const kedinasanRek = partner?.data?.rekomendasi_kedinasan || [];
  const utbk = n?.utbk_subtes || [];
  const citaCita = data?.assessment?.cita_cita;

  const baseByKelas = {
    9: [
      { t: "Ikuti Asesmen Potensi Diri", d: "Kenali karakter, kebiasaan belajar & kekuatan kognitifmu.", done: hasAssessment, to: "/asesmen" },
      { t: "Eksplorasi minimal 3 bidang", d: "Buka menu Jelajahi Bidang dan tandai yang menarik.", done: hasBidang, to: "/jelajahi-bidang" },
      { t: "Diskusikan hasil dengan orang tua", d: "Bagikan gambaran potensimu untuk arah SMA.", done: false },
      { t: "Coba TryOut TKA", d: "Ukur kemampuan akademik dasarmu.", done: hasTryout, to: "/tryout" },
    ],
    10: [
      { t: "Selesaikan Asesmen Potensi Diri", d: "Dapatkan gambaran presisi posisimu.", done: hasAssessment, to: "/asesmen" },
      { t: "Pilih 4–5 mapel pilihan", d: "Sesuaikan dengan bidang yang kamu minati.", done: hasMapel, to: "/jelajahi-bidang" },
      { t: "Tentukan bidang minat utama", d: "Tandai satu bidang di Jelajahi Bidang.", done: hasBidang, to: "/jelajahi-bidang" },
      { t: "Ikuti TryOut TKA", d: "Latih kemampuan akademik secara berkala.", done: hasTryout, to: "/tryout" },
    ],
    11: [
      { t: "Perdalam mapel spesialisasi", d: "Fokus pada mapel pendukung bidangmu.", done: hasMapel, to: "/jelajahi-bidang" },
      { t: "Bangun portofolio & prestasi", d: "Ikuti lomba/proyek sesuai bidang.", done: false },
      { t: "Kenali jalur SNBP/SNBT", d: "Pahami syarat & strategi masuk PTN.", done: false },
      { t: "Ikuti TryOut UTBK", d: "Ukur kesiapan penalaran skolastikmu.", done: hasTryout, to: "/tryout" },
    ],
    12: [
      { t: "Finalkan 2–3 pilihan jurusan & PTN", d: "Mantapkan berdasarkan hasil asesmen.", done: hasBidang, to: "/jelajahi-bidang" },
      { t: "Ikuti TryOut TKA & UTBK rutin", d: "Ulangi berkala dan pantau skormu.", done: hasTryout, to: "/tryout" },
      { t: "Susun strategi belajar dari hasil", d: "Perbaiki subtes dengan skor terendah.", done: hasAssessment },
      { t: "Siapkan berkas SNBP & SNBT", d: "Lengkapi dokumen pendaftaran.", done: false },
    ],
  };
  const steps = baseByKelas[kelas] || baseByKelas[10];

  const suggestions = Array.from(new Set([
    ...aiSteps,
    ...(favorite?.langkah || []),
    ...mapelPrioritas.map((m) => `Kuatkan mapel ${m.mapel}`),
    ...(jalurRek ? [`Pelajari syarat & strategi ${jalurRek.nama}`] : []),
    ...steps.map((s) => s.t),
  ].filter(Boolean)));

  const toggleSel = (s) => setRencanaSel((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const toggleMapel = (m) => setMapelSel((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  const saveMapel = async () => {
    setSavingMapel(true);
    try {
      await api.post("/journey/save", { mapel_pilihan: mapelSel, bidang_minat: data?.journey?.bidang_minat || null, kelas });
      toast.success("Pilihan mapel tersimpan");
      const r = await api.get("/dashboard/overview"); setData(r.data);
    } catch { toast.error("Gagal menyimpan mapel"); }
    finally { setSavingMapel(false); }
  };
  const mapelText = (kelas && kelas <= 9)
    ? { title: "Mapel yang Ingin Kamu Perkuat", desc: "Pilih mapel yang ingin kamu kuatkan sebagai bekal menuju SMA." }
    : kelas === 10
    ? { title: "Prioritas Mapel Utama di Kelas 10", desc: "Di Kurikulum Merdeka, di kelas 10 kamu masih belajar semua mapel. Tandai prioritas utamamu sekarang sebagai gambaran memilih peminatan nanti di kelas 11." }
    : { title: "Mapel Pilihan (Peminatan)", desc: "Pilih mapel pilihan/peminatan yang paling menunjang bidang & targetmu di kelas 11–12." };
  const addCustom = () => { const v = customStep.trim(); if (v && !rencanaSel.includes(v)) { setRencanaSel((c) => [...c, v]); setCustomStep(""); } };
  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await api.post("/journey/target", { ptn: targetPtn, prodi: targetProdi });
      toast.success("Target pendidikan tersimpan — tampil di Profilku");
      const r = await api.get("/dashboard/overview"); setData(r.data);
    } catch { toast.error("Gagal menyimpan target"); }
    finally { setSavingTarget(false); }
  };
  const saveRencana = async () => {
    setSavingRencana(true);
    try {
      await api.post("/journey/rencana", { rencana: rencanaSel });
      toast.success("Rencanaku tersimpan");
      const r = await api.get("/dashboard/overview"); setData(r.data);
    } catch { toast.error("Gagal menyimpan rencana"); }
    finally { setSavingRencana(false); }
  };

  return (
    <AppShell>
      <div className="mb-8 max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] text-sm font-medium mb-3"><Sparkles className="h-4 w-4" /> {user?.role === "orangtua" ? "Panduan orang tua" : `Rencana untuk Kelas ${kelas || "-"}`}</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">Susun Langkah</h1>
        <p className="text-[#5C5F5C] mt-2">Langkah nyata yang bisa kamu jalankan sekarang, disesuaikan dengan jenjang kelasmu.</p>
      </div>

      {/* Posisi dalam perjalanan + instruksi */}
      {user?.role !== "orangtua" && (
        <JourneyPosition current="susun" note={
          <>
            <p className="text-sm text-[#1A1C1A] leading-relaxed">
              <span className="font-semibold">Kamu ada di sini sekarang.</span> Di sinilah semua yang kamu temukan dirangkai jadi rencana nyata.
              Berdasarkan <span className="font-semibold">hasil asesmen</span> dan <span className="font-semibold">target impianmu</span>, lengkapi mapel prioritas, target pendidikan, lalu susun langkah-langkahmu di bawah ini.
            </p>
            <p className="text-sm text-[#5C5F5C] mt-2">Tenang — semuanya bisa kamu ubah dan sesuaikan kapan saja seiring perkembanganmu. 🌱</p>
          </>
        } />
      )}

      <TalentaCTA className="mb-8" title="Butuh pendampingan menyusun langkah?" sub="Konsultasi arah studi, latihan & TryOut bareng mentor di Bimbel Talenta." />

      {/* Prioritas: Profesi Impian */}
      {favorite && (
        <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A] mb-8" data-testid="favorite-priority">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4B942]/20 text-[#F4B942] text-sm font-medium mb-3"><Star className="h-4 w-4 fill-[#F4B942]" /> Profesi impianmu</div>
          <h2 className="font-heading font-black text-2xl mb-1">{favorite.nama}</h2>
          {favorite.sehari_hari && <p className="text-white/80 text-sm mb-4">{favorite.sehari_hari}</p>}
          <p className="text-white/70 text-sm mb-3">Langkah prioritas menuju profesi ini:</p>
          <ol className="space-y-3">
            {(favorite.langkah || []).map((st, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`favorite-step-${i}`}>
                <span className="h-7 w-7 rounded-full bg-[#F4B942] text-[#1A1C1A] grid place-items-center text-sm font-bold shrink-0">{i + 1}</span>
                <span className="text-sm text-white/90 pt-0.5">{st}</span>
              </li>
            ))}
          </ol>
          <Link to="/profilku" className="mt-4 inline-flex items-center gap-2 text-sm text-[#F4B942] font-medium">Ganti profesi impian di Profilku <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}

      {/* Personalisasi dari hasil asesmen */}
      {hasAssessment && (citaCita || mapelPrioritas.length > 0 || jalurRek) && (
        <div className="clay-card p-7 bg-[#4A6B53] text-white border-[#4A6B53] mb-8" data-testid="langkah-personal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[#F4B942] text-sm font-medium mb-4"><Sparkles className="h-4 w-4" /> Fokus dari hasil asesmenmu</div>
          <div className="grid md:grid-cols-3 gap-5">
            {citaCita && (
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/70 text-xs mb-1 flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Cita-cita</p>
                <p className="font-heading font-bold">{citaCita}</p>
              </div>
            )}
            {mapelPrioritas.length > 0 && (
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/70 text-xs mb-1 flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Mapel prioritas</p>
                <p className="text-sm">{mapelPrioritas.map((m) => m.mapel).join(", ")}</p>
              </div>
            )}
            {jalurRek && (
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/70 text-xs mb-1 flex items-center gap-1.5"><Route className="h-3.5 w-3.5" /> Jalur disarankan</p>
                <p className="text-sm font-medium">{jalurRek.nama}</p>
              </div>
            )}
          </div>
          <Link to="/profilku" className="mt-4 inline-flex items-center gap-2 text-sm text-[#F4B942] font-medium">Lihat hasil lengkap <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}

      {/* Form: Target Pendidikan Impian */}
      {user?.role !== "orangtua" && (
        <div className="clay-card p-7 mb-8" data-testid="target-pendidikan-form">
          <div className="flex items-center gap-2 mb-1"><GraduationCap className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Target Pendidikan Impian</h2></div>
          <p className="text-sm text-[#5C5F5C] mb-4">Tuliskan PTN & prodi impianmu. Target ini akan tampil di Profilku sebagai arah tujuan belajarmu.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={targetProdi} onChange={(e) => setTargetProdi(e.target.value)} data-testid="target-prodi-input"
              placeholder="Prodi impian (mis. Arsitektur)" className="h-11 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none text-[#1A1C1A]" />
            <input value={targetPtn} onChange={(e) => setTargetPtn(e.target.value)} data-testid="target-ptn-input"
              placeholder="PTN impian (mis. ITB)" className="h-11 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none text-[#1A1C1A]" />
          </div>
          <button onClick={saveTarget} disabled={savingTarget} data-testid="save-target-btn"
            className="mt-4 px-6 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors disabled:opacity-60 flex items-center gap-2">
            <Save className="h-4 w-4" /> {savingTarget ? "Menyimpan…" : "Simpan Target"}
          </button>
        </div>
      )}

      {/* Kelas 12: Kesesuaian Jalur Pendidikan (PTN/Kedinasan) & Prodi — pengganti mapel pilihan */}
      {user?.role !== "orangtua" && kelas === 12 && (
        <div className="clay-card p-7 mb-8" data-testid="jalur-form">
          <div className="flex items-center gap-2 mb-1"><Route className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Kesesuaian Jalur Pendidikan</h2></div>
          <p className="text-sm text-[#5C5F5C] mb-4">Di kelas 12 tidak ada lagi pemilihan mapel. Fokusmu adalah memilih jalur ke PTN atau Kedinasan beserta prodinya, selaras dengan hasil asesmen.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#E5E0D8] p-4">
              <p className="text-sm font-semibold text-[#1A1C1A] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#E25E3E]" /> Jalur PTN (SNBP / SNBT / Mandiri)</p>
              {jalurRek?.nama && <p className="text-xs text-[#5C5F5C] mb-2">Disarankan: <span className="font-medium text-[#1A1C1A]">{jalurRek.nama}</span></p>}
              {prodiRek.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="jalur-prodi-list">{prodiRek.map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] font-medium">{p}</span>)}</div>
              ) : <p className="text-xs text-[#9C9A94]">Selesaikan asesmen atau upload laporan iTi untuk melihat prodi yang sesuai.</p>}
              {utbk.length > 0 && <p className="text-xs text-[#5C5F5C] mt-3">Subtes UTBK yang perlu dikuatkan: <span className="text-[#1A1C1A]">{utbk.join(", ")}</span></p>}
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-4">
              <p className="text-sm font-semibold text-[#1A1C1A] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#4A6B53]" /> Jalur Kedinasan</p>
              {kedinasanRek.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="jalur-kedinasan-list">{kedinasanRek.map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] font-medium">{p}</span>)}</div>
              ) : <p className="text-xs text-[#9C9A94]">Belum ada rekomendasi kedinasan dari asesmen iTi.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Form: Pilih Mapel (bahasa menyesuaikan kelas — Kurikulum Merdeka) */}
      {user?.role !== "orangtua" && kelas !== 12 && (
        <div className="clay-card p-7 mb-8" data-testid="mapel-form">
          <div className="flex items-center gap-2 mb-1"><BookOpen className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">{mapelText.title}</h2></div>
          <p className="text-sm text-[#5C5F5C] mb-4">{mapelText.desc}</p>
          {mapelPrioritas.length > 0 && (
            <div className="mb-4 rounded-xl bg-[#F4B942]/10 border border-[#F4B942]/30 p-4">
              <p className="text-xs font-semibold text-[#8a6d16] mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Anjuran sistem dari hasil asesmenmu</p>
              <div className="flex flex-wrap gap-1.5">
                {mapelPrioritas.map((m, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white text-[#1A1C1A] border border-[#E5E0D8]">{m.mapel}</span>)}
              </div>
            </div>
          )}
          <p className="text-xs font-semibold text-[#5C5F5C] mb-2">Pilihanmu ({mapelSel.length} dipilih):</p>
          <div className="flex flex-wrap gap-2">
            {mapelOpts.map((m) => {
              const on = mapelSel.includes(m);
              return (
                <button key={m} onClick={() => toggleMapel(m)} data-testid={`mapel-opt-${m}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-[#E25E3E] text-white border-[#E25E3E]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#E25E3E]"}`}>
                  {m}
                </button>
              );
            })}
          </div>
          <button onClick={saveMapel} disabled={savingMapel} data-testid="save-mapel-btn"
            className="mt-4 px-6 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2">
            <Save className="h-4 w-4" /> {savingMapel ? "Menyimpan…" : (kelas === 10 ? "Simpan Prioritas Mapel" : "Simpan Pilihan Mapel")}
          </button>
        </div>
      )}

      {/* Susun Rencanaku — pilih anjuran & buat rencana sendiri */}
      <div className="clay-card p-7 mb-8" data-testid="rencana-builder">
        <div className="flex items-center gap-2 mb-1"><ListChecks className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Susun Rencanaku</h2></div>
        <p className="text-sm text-[#5C5F5C] mb-5">Pilih anjuran yang paling cocok denganmu, tambah targetmu sendiri, lalu simpan sebagai rencana pribadi.</p>
        <div className="space-y-2">
          {suggestions.map((sg, i) => {
            const on = rencanaSel.includes(sg);
            return (
              <button key={i} onClick={() => toggleSel(sg)} data-testid={`rencana-opt-${i}`}
                className={`w-full text-left px-4 py-3 rounded-xl border flex items-center gap-3 transition-colors ${on ? "border-[#4A6B53] bg-[#4A6B53]/10" : "border-[#E5E0D8] hover:border-[#4A6B53]/50"}`}>
                <span className={`h-6 w-6 rounded-md grid place-items-center shrink-0 ${on ? "bg-[#4A6B53] text-white" : "bg-[#F0ECE4] text-transparent"}`}><Check className="h-4 w-4" /></span>
                <span className="text-sm text-[#1A1C1A]">{sg}</span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 mt-4">
          <input value={customStep} onChange={(e) => setCustomStep(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} data-testid="rencana-custom-input"
            placeholder="Tambah targetmu sendiri…" className="flex-1 h-11 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none text-[#1A1C1A]" />
          <button onClick={addCustom} data-testid="rencana-add-btn" className="px-4 rounded-xl bg-[#1A1C1A] text-white hover:opacity-90 transition-opacity"><Plus className="h-4 w-4" /></button>
        </div>
        <button onClick={saveRencana} disabled={savingRencana || rencanaSel.length === 0} data-testid="rencana-save-btn"
          className="mt-4 px-6 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#C94F31] transition-colors flex items-center gap-2">
          <Save className="h-4 w-4" /> {savingRencana ? "Menyimpan…" : "Simpan Rencanaku"}
        </button>
        {savedRencana.length > 0 && (
          <div className="mt-6 border-t border-[#E5E0D8] pt-5" data-testid="rencana-saved">
            <p className="text-sm font-semibold text-[#1A1C1A] mb-3">Rencanaku ({savedRencana.length})</p>
            <ol className="space-y-2">
              {savedRencana.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#1A1C1A]"><span className="h-6 w-6 rounded-full bg-[#4A6B53] text-white grid place-items-center text-xs font-bold shrink-0">{i + 1}</span><span className="pt-0.5">{r}</span></li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {aiSteps.length > 0 && (
        <div className="mt-10 clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A]">
          <h3 className="font-heading font-black text-xl mb-3 flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#F4B942]" /> Saran khusus dari hasil asesmenmu</h3>
          <ul className="space-y-2">
            {aiSteps.map((a, i) => <li key={i} className="flex items-start gap-2 text-white/90 text-sm"><span className="text-[#F4B942]">→</span> {a}</li>)}
          </ul>
        </div>
      )}

      {!hasAssessment && (
        <div className="mt-8 clay-card p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-8 w-8 text-[#E25E3E]" />
            <p className="text-sm text-[#5C5F5C] max-w-md">Ingin langkah yang lebih presisi? Selesaikan Asesmen Potensi Diri untuk mendapat saran yang dipersonalisasi.</p>
          </div>
          <Link to="/asesmen" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors">Mulai Asesmen</Link>
        </div>
      )}
    </AppShell>
  );
}
